import { createApi } from "@reduxjs/toolkit/query/react";
import customBaseQuery from "../../../utils/customBaseQuery";
import { gql } from "graphql-request";

export const QuestionsApi = createApi({
  reducerPath: "questionApi",
  baseQuery: customBaseQuery,
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getAllQuestionsApi: builder.query({
      query: ({
        pageNumber,
        pageSize,
        searchFilters,
        user,
        stakeholderRole,
        sortOrder = "DESC",
        sortKey
      }: any) => {
        return {
          url: "/graphql",
          body: gql`  
          query {
            
            getAllQuestions(
              searchFilters: {
                    indices:${JSON.stringify(searchFilters?.indices)},
                    status:${JSON.stringify(searchFilters?.status)},
                    searchTags:{defaultTag:${searchFilters?.searchTags?.defaultTag ? JSON.stringify(searchFilters?.searchTags?.defaultTag) : null}, bloomTaxonomy:${searchFilters?.searchTags?.bloomTaxonomy ? JSON.stringify(searchFilters?.searchTags?.bloomTaxonomy) : null} },
                    sourceType:${searchFilters.sourceType ? JSON.stringify(searchFilters?.sourceType) : null},
                    sourceName:${searchFilters.sourceName ? JSON.stringify(searchFilters?.sourceName) : null},
                    sourceNames:${searchFilters.sourceNames ? JSON.stringify(searchFilters?.sourceNames) : null},
                    skills:${JSON.stringify(searchFilters?.skills)},
                    difficultyLevel:${JSON.stringify(searchFilters?.difficultyLevel)},
                    languageList:${JSON.stringify(searchFilters?.languageList?.length > 0 ? searchFilters?.languageList : [])},
                    subTypes:${JSON.stringify(searchFilters?.subTypes)},
                    stakeHolder:{ ${stakeholderRole}:${JSON.stringify(user)}},
                    title:${JSON.stringify(searchFilters?.title)},
                    access:${JSON.stringify(searchFilters?.access)},
                    customVisibility:${searchFilters.customVisibility ? JSON.stringify(searchFilters?.customVisibility) : null},
                    maxMarks:${searchFilters.maxMarks ? JSON.stringify(searchFilters?.maxMarks) : null},
                    exclude:${searchFilters.exclude && searchFilters.exclude.length > 0 ? JSON.stringify(searchFilters?.exclude) : null},   
                    reviewRequired:${JSON.stringify(searchFilters?.reviewRequired != null ? searchFilters?.reviewRequired : null)},
                    authors:${searchFilters.authors && searchFilters.authors.length > 0 ? JSON.stringify(searchFilters?.authors) : null},
                    allowedCodingLanguages: ${JSON.stringify(searchFilters?.allowedCodingLanguages?.length > 0 ? searchFilters?.allowedCodingLanguages : [])}
                    }
              pageNumber: ${pageNumber}
              pageSize: ${pageSize}
              sortKey: ${JSON.stringify(sortKey)}
              sortOrder: ${JSON.stringify(sortOrder)}
              requireNonExpiredData: true
            ) {
              timestamp
              error
              version
              data {
                pageNumber
                pageSize
                totalCount
                searchResult {
                  allowedcodinglanguages
                  problemstatement
                  expirydate
                  createdby
                  questionid
                  title
                  updatedby
                  language
                  access
                  versionscreated
                  version
                  searchtags{
                  defaultTags
                  bloomTaxonomy
                  }
                  language
                  accessibility
                  clonedversion
                  skills
                  publishedby
                  latestpublishedversion
                  updatedat
                  createdat
                  hints
                  showhint
                  explanation
                  showexplanation
                  cloneid
                  practicequestion
                  negativemarkschema
                  negativemarkenabled
                  partialmarkschema
                  partialmarkingenabled
                  reviewrequired
                  fibdata
                  active
                  options
                  solution
                  archived
                  stakeholder {
                    owner
                    observer
                    reviewer
                  }
                  startercode {
                    code
                    language
                  }
                  subtype
                  rubricid
                  maxmarks
                  createdat
                  status
                  publishedat
                  sourcename
                  sourcetype
                  versionscreated
                  templateid
                  createdzoneid
                  updatedzoneid
                  org
                  duration
                  difficultylevel
                  caseSensitive
                  submissiontype
                }
              }
            }
          }
        `
        };
      },
      extraOptions: { service: "MCQ_Assessment" },
      serializeQueryArgs: ({ queryArgs }) => {
        const {
          searchFilters,
          sortKey,
          sortOrder,
          pageNumber,
          pageSize,
          isInfiniteScroll = true
        } = queryArgs;

        // Infinite scroll (default)
        if (isInfiniteScroll) {
          return JSON.stringify({
            searchFilters,
            sortKey,
            sortOrder,
            pageSize
          });
        }

        // Normal pagination
        return JSON.stringify({
          searchFilters,
          sortKey,
          sortOrder,
          pageNumber,
          pageSize
        });
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg.pageNumber !== previousArg.pageNumber;
      },
      merge(currentCacheData, responseData, otherArgs) {
        const { isInfiniteScroll = true, pageNumber } = otherArgs.arg;

        if (isInfiniteScroll && pageNumber > 1) {
          const messageMap = new Map();

          [
            ...currentCacheData.getAllQuestions.data.searchResult,
            ...responseData.getAllQuestions.data.searchResult
          ].forEach((msg) => {
            const key = `${msg.questionid}_${msg.version}`;

            if (!messageMap.has(key)) {
              messageMap.set(key, msg);
            }
          });

          return {
            ...responseData,
            getAllQuestions: {
              ...responseData.getAllQuestions,
              data: {
                ...responseData.getAllQuestions.data,
                searchResult: [...messageMap.values()]
              }
            }
          };
        }

        return responseData;
      }
    }),
    getMcqQL: builder.query({
      query: ({ questionId, version }: any) => {
        return {
          url: "/graphql",
          body: gql`
            query {
              getMcqQL(questionid: "${questionId}", version: ${version}) {
                timestamp
                data {
                  options
                  questionId
                  solution
                   maxMarks
                  difficultyLevel
                  duration
                  access
                  subType
                  language
                  accessibility
                  versionsCreated
                  version
                  reviewRequired
                  status
                  latestPublishedVersion
                  expiryDate
                  searchTags{
                    defaultTags
                    bloomTaxonomy
                      }
                  skills
                  hints
                  showHint
                  sourceName
                  updatedat
                  sourceType
                  stakeholder{
                    owner
                    observer
                    reviewer
                      }
                  showExplanation
                  explanation
                  title
                  problemStatement
                }
              }
            }
          `
        };
      },
      extraOptions: { service: "MCQ_Assessment" }
    }),
    getFibQL: builder.query({
      query: ({ questionId, version }: any) => {
        return {
          url: "/graphql",
          body: gql`
            query {
              getFibQL(questionid: "${questionId}", version: ${version}) {
                timestamp
                data {
                  options
                  questionId
                  fibData
                  title
                  difficultyLevel
                  duration
                  maxMarks
                  access
                  subType
                  hints
                  showHint
                  reviewRequired
                  status
                  latestPublishedVersion
                  showExplanation
                  explanation
                  language
                  accessibility
                  expiryDate
                  versionsCreated
                  version
                  updatedat
                  sourceName
                  sourceType
                  searchTags{
                    defaultTags
                    bloomTaxonomy
                  }
                    skills
                  stakeholder{
                    owner
                    observer
                    reviewer
                  }
                  problemStatement
                  caseSensitive
                }
              }
            }
          `
        };
      },
      extraOptions: { service: "MCQ_Assessment" }
    }),
    getCodingQL: builder.query({
      query: ({ questionId, version }: any) => {
        return {
          url: "/graphql",
          body: gql`
            query {
              getCodingQL(questionid: "${questionId}", version: ${version}) {
                  timestamp
                  error
                  version
                  data {
                      questionId
                      title
                      problemStatement
                      maxMarks
                      difficultyLevel
                      expiryDate
                      duration
                      language
                      accessibility
                      status
                      cloneId
                      clonedVersion
                      skills
                      practiceQuestion
                      publishedAt
                      publishedBy
                      updatedby
                      updatedat
                      createdat
                      createdby
                      updatedzoneid
                      createdzoneid
                      org
                      approvalRequired
                      hints
                      showHint
                      explanation
                      showExplanation
                      active
                      sourceName
                      sourceType
                      templateId
                      archived
                      version
                      versionsCreated
                      latestPublishedVersion
                      latestDraftVersion
                      reviewRequired
                      access
                      totalTestcases
                      solution
                      starterCode
                      allowedCodingLanguages
                      searchTags {
                          defaultTags
                          bloomTaxonomy
                          contentUsage
                          contentDomain
                          contentRegion
                          contentCategory
                          contentSubCategory
                      }
                      stakeholder {
                          owner
                          observer
                          reviewer
                      }
                  }
              }
            }
          `
        };
      },
      extraOptions: { service: "MCQ_Assessment" }
    }),
    getSubjectiveQL: builder.query({
      query: ({ questionId, version }: any) => {
        return {
          url: "/graphql",
          body: gql`
            query GetSubjectiveQn {
              getSubjectiveQn(
                questionid: "${questionId}"
                version: ${version}
              ) {
                timestamp
                error
                version
                data {
                  questionId
                  version
                  title
                  problemStatement
                  maxMarks
                  difficultyLevel
                  duration
                  language
                  accessibility
                  status
                  skills
                  expiryDate
                  practiceQuestion
                  hints
                  showHint
                  explanation
                  showExplanation
                  cloneid
                  clonedversion
                  publishedAt
                  publishedzoneId
                  publishedBy
                  solution
                  rubricid
                  submissiontype
                  active
                  aiEvaluated
                  access
                  sourcename
                  sourcetype
                  templateid
                  archived
                  versionscreated
                  latestPublishedVersion
                  latestDraftVersion
                  reviewRequired
                  updatedby
                  updatedat
                  createdat
                  createdby
                  updatedzoneid
                  createdzoneid
                  org
                  searchTags {
                    defaultTags
                    bloomTaxonomy
                  }
                  stakeholder {
                    owner
                    observer
                    reviewer
                  }
                }
              }
            }
          `
        };
      },
      extraOptions: { service: "MCQ_Assessment" }
    })
  })
});

export const {
  useGetAllQuestionsApiQuery,
  useLazyGetAllQuestionsApiQuery,
  useGetMcqQLQuery,
  useLazyGetMcqQLQuery,
  useGetFibQLQuery,
  useLazyGetFibQLQuery,
  useGetCodingQLQuery,
  useLazyGetCodingQLQuery,
  useLazyGetSubjectiveQLQuery
} = QuestionsApi;
